import { Entity } from "excalibur";
import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { GetPlayable } from "../constants";
import { HandCombiningInfo } from "../deck";
import { TargetableComponent } from "../ecs/target";
import { useTouchDrag } from "../hooks/useTouchDrag";
import {
  Card,
  CardId,
  CardKind,
  CardKinds,
  CardLevel,
  DynamicResources,
  GameResources,
} from "../types";
import { isMobile } from "../utils/mobile";
import { getResourceSprite, getValueColor } from "../utils/tileOverlayUtils";
import "./Hud.css";
import { PlayableTooltip } from "./PlayableTooltip";
import Sprite from "./Sprite";

export type CardProps = Card & {
  selected: boolean;
  onDragStart?: (e: React.DragEvent, cardId: CardId) => void;
  onDragEnd?: (e: React.DragEvent) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDragEnter?: (e: React.DragEvent, cardId: CardId) => void;
  onDragLeave?: (e: React.DragEvent, cardId: CardId) => void;
  onDrop?: (e: React.DragEvent) => void;
  onClick?: (cardId: CardId) => void;
  onMouseEnter?: (e: React.MouseEvent) => void;
  onMouseLeave?: () => void;
  onMouseMove?: (e: React.MouseEvent) => void;
  onTouchStart?: (e: React.TouchEvent, cardId: CardId) => void;
  onTouchMove?: (e: React.TouchEvent) => void;
  onTouchEnd?: (e: React.TouchEvent, cardId: CardId) => void;
  isDragging?: boolean;
  isHovered?: boolean; // true when being hovered during drag operation
  isCombinable?: boolean; // true if part of a group of 2+ consecutive identical cards
  index: number;
  leftPosition: number;
};

const CardResourceDisplay: FC<{ resources: DynamicResources }> = ({
  resources,
}) => {
  return (
    <div className="flex-1 flex flex-col justify-end items-center pb-1 px-1 gap-0.5">
      {GameResources.map((resource) =>
        resources[resource] !== 0 ? (
          <div
            key={resource}
            className={`flex items-center gap-1 ${getValueColor(resources[resource])} font-bold text-xs leading-none bg-black/60 px-1 py-0 rounded`}
          >
            <Sprite
              filename="/images/resources_20.png"
              {...getResourceSprite(resource)}
              width={16}
              height={16}
              className="inline-block"
            />
            <span>{resources[resource]}</span>
          </div>
        ) : null,
      )}
    </div>
  );
};

function cardSprite({ kind, level }: { kind: CardKind; level: CardLevel }) {
  const spriteIdx = CardKinds.indexOf(kind);
  return { y: spriteIdx, x: level };
}

function getCardBorderClasses(
  selected: boolean,
  isCombinable: boolean,
  isHovered: boolean,
): string {
  if (selected) {
    return "border-4 border-green-600 shadow-lg"; // Active/selected card
  }
  if (isHovered) {
    return "border-4 border-blue-500 shadow-lg"; // Drag hover target
  }
  if (isCombinable) {
    return "border-4 border-yellow-500 shadow-md"; // 2 consecutive cards (almost ready to combine)
  }
  return "border-4 border-gray-300 hover:border-black";
}

function getCardTextClasses(selected: boolean): string {
  return selected ? "text-red-900 font-semibold" : "";
}

function getCardHoverClasses(selected: boolean): string {
  return !selected ? "hover:border-blue-400" : "";
}

const CardEl: FC<CardProps> = (props) => {
  const [currentPosition, setCurrentPosition] = useState(props.leftPosition);
  const borderClasses = getCardBorderClasses(
    props.selected,
    props.isCombinable ?? false,
    props.isHovered ?? false,
  );
  const textClasses = getCardTextClasses(props.selected);
  const hoverClasses = getCardHoverClasses(props.selected);
  const opacity = props.isDragging ? "opacity-50" : "opacity-100";
  const playable = useMemo(() => GetPlayable(props.name), [props.name]);
  const filename = "images/playables.png";

  // Update position with a delay to ensure transition works
  useEffect(() => {
    if (currentPosition !== props.leftPosition) {
      requestAnimationFrame(() => {
        setCurrentPosition(props.leftPosition);
      });
    }
  }, [props.leftPosition, currentPosition]);

  // Outer container handles positioning and animation
  const containerStyle: React.CSSProperties = {
    position: "absolute",
    transform: `translateX(${currentPosition}px)`,
  };

  return (
    <div className="card-container" style={containerStyle}>
      <div
        className={`pointer-events-auto user-select-none box-border ${borderClasses} ${hoverClasses} w-12 h-20 sm:w-16 sm:h-32 cursor-move ${opacity}`}
        data-testid="hand-card"
        draggable={!isMobile()}
        onDragStart={
          !isMobile() ? (e) => props.onDragStart?.(e, props.cardId) : undefined
        }
        onDragEnd={!isMobile() ? (e) => props.onDragEnd?.(e) : undefined}
        onDragOver={!isMobile() ? (e) => props.onDragOver?.(e) : undefined}
        onDragEnter={
          !isMobile() ? (e) => props.onDragEnter?.(e, props.cardId) : undefined
        }
        onDragLeave={
          !isMobile() ? (e) => props.onDragLeave?.(e, props.cardId) : undefined
        }
        onDrop={!isMobile() ? (e) => props.onDrop?.(e) : undefined}
        onClick={!isMobile() ? () => props.onClick?.(props.cardId) : undefined}
        onTouchStart={
          isMobile() ? (e) => props.onTouchStart?.(e, props.cardId) : undefined
        }
        onTouchMove={isMobile() ? (e) => props.onTouchMove?.(e) : undefined}
        onTouchEnd={
          isMobile() ? (e) => props.onTouchEnd?.(e, props.cardId) : undefined
        }
        onMouseEnter={props.onMouseEnter}
        onMouseLeave={props.onMouseLeave}
        onMouseMove={props.onMouseMove}
      >
        <div
          className={`text-center w-full h-full flex flex-col ${textClasses}`}
        >
          {/* Sprite at top */}
          <div className="flex-shrink-0 mt-1 sm:mt-2">
            <Sprite
              filename={filename}
              height={32}
              width={32}
              {...cardSprite(playable)}
              className="mx-auto"
            />
          </div>

          {/* Label in middle */}
          <div className="flex-shrink-0 px-1">
            <span className="text-xs sm:text-sm">{playable.label}</span>
          </div>

          {/* Resources at bottom */}
          <CardResourceDisplay resources={playable.resources} />
        </div>
      </div>
    </div>
  );
};

export const Hand: FC<{
  cards: Card[];
  swapCards: (a: CardId, b: CardId) => void;
  moveCard?: (cardId: CardId, newIndex: number) => void;
  combiningInfo?: HandCombiningInfo;
  hoverTarget?: Entity<TargetableComponent>;
}> = ({ cards, swapCards, moveCard, combiningInfo, hoverTarget }) => {
  const [draggedCardId, setDraggedCardId] = useState<CardId | null>(null);
  const [tooltipCardId, setTooltipCardId] = useState<CardId | null>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [hoveredCardId, setHoveredCardId] = useState<CardId | null>(null);
  const [insertionIndex, setInsertionIndex] = useState<number | null>(null);
  const mobile = isMobile();
  const handRef = useRef<HTMLDivElement>(null);

  // Touch drag using custom hook
  const { touchHandlers } = useTouchDrag({
    isEnabled: mobile,
    onDragStart: (cardId) => {
      setDraggedCardId(cardId);
    },
    onDragMove: (x, _y, _cardId) => {
      const insertPosition = calculateInsertPosition(x);
      setInsertionIndex(insertPosition);
      setHoveredCardId(null);
    },
    onDragEnd: (x, _y, cardId, wasDragging) => {
      if (wasDragging && moveCard && draggedCardId !== null) {
        const insertPosition = calculateInsertPosition(x);
        const currentIndex = cards.findIndex(c => c.cardId === draggedCardId);
        const adjustedPosition = insertPosition > currentIndex ? insertPosition - 1 : insertPosition;
        
        if (adjustedPosition !== currentIndex) {
          moveCard(draggedCardId, adjustedPosition);
        }
      } else if (!wasDragging) {
        handleClick(cardId);
      }
      
      // Reset all drag state
      setDraggedCardId(null);
      setHoveredCardId(null);
      setInsertionIndex(null);
    }
  });

  // Calculate which position a card should be inserted at based on X coordinate
  const calculateInsertPosition = (mouseX: number): number => {
    if (!handRef.current || cards.length === 0) return 0;

    const handRect = handRef.current.getBoundingClientRect();
    const relativeX = mouseX - handRect.left;

    // Calculate card positions similar to getCardPosition
    const isDesktop = window.innerWidth >= 640;
    const cardWidth = isDesktop ? 64 : 48;
    const gap = 8;
    const extraSpacing = 40;

    // Check if dropping before the first card
    if (relativeX < cardWidth / 2) return 0;

    // Check positions after the first card
    for (let i = 1; i < cards.length; i++) {
      const cardStartX = cardWidth + extraSpacing + (i - 1) * (cardWidth + gap);
      const cardCenterX = cardStartX + cardWidth / 2;

      if (relativeX < cardCenterX) return i;
    }

    // Drop at the end
    return cards.length;
  };

  // Simple function to calculate card position
  const getCardPosition = (index: number) => {
    // Responsive card width: 48px (w-12) on mobile, 64px (w-16) on desktop
    // Gap: 16px (gap-4)
    // Extra spacing after active card: 16px (w-4)
    const isDesktop = window.innerWidth >= 640; // sm breakpoint
    const cardWidth = isDesktop ? 64 : 48;
    const gap = 8;
    const extraSpacing = 40; // extra space after active card for better visual separation

    if (index === 0) {
      return 0; // Active card at left
    } else if (index <= cards.length) {
      // Cards after active card: card 0 position + card width + extra spacing + (index-1) * (card width + gap)
      return cardWidth + extraSpacing + (index - 1) * (cardWidth + gap);
    } else {
      // Position after the last card
      return (
        cardWidth +
        extraSpacing +
        (cards.length - 1) * (cardWidth + gap) +
        cardWidth +
        gap
      );
    }
  };

  // Auto-dismiss tooltip when there's something more important to see (hoverTarget active)
  useEffect(() => {
    if (hoverTarget && tooltipCardId) {
      setTooltipCardId(null);
    }
  }, [hoverTarget, tooltipCardId]);

  // Global click handler for tooltips (mobile only)
  useEffect(() => {
    if (!mobile || !tooltipCardId) return;

    const handleClickAnywhere = (_event: MouseEvent) => {
      // Dismiss tooltip when clicking anywhere on mobile
      setTooltipCardId(null);
    };

    document.addEventListener("mousedown", handleClickAnywhere);
    return () => document.removeEventListener("mousedown", handleClickAnywhere);
  }, [mobile, tooltipCardId]);

  const handleDragStart = (e: React.DragEvent, cardId: CardId) => {
    setDraggedCardId(cardId);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragEnd = (_e: React.DragEvent) => {
    setDraggedCardId(null);
    setHoveredCardId(null);
    setInsertionIndex(null);
  };


  const handleCardDragEnter = (e: React.DragEvent, cardId: CardId) => {
    e.preventDefault();
    if (draggedCardId && draggedCardId !== cardId) {
      setHoveredCardId(cardId);
      setInsertionIndex(null); // Clear insertion index when hovering over a card
    }
  };

  const handleCardDragLeave = (e: React.DragEvent, cardId: CardId) => {
    e.preventDefault();
    if (hoveredCardId === cardId) {
      setHoveredCardId(null);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    // Don't stop propagation - let the background handler deal with it
    // This way the drop position is based on coordinates, not which card was hit
  };

  const handleBackgroundDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (draggedCardId !== null && moveCard) {
      const insertPosition = calculateInsertPosition(e.clientX);
      const currentIndex = cards.findIndex((c) => c.cardId === draggedCardId);

      // Adjust position if dragging to a position after the current position
      // (since removing the card shifts indices)
      const adjustedPosition =
        insertPosition > currentIndex ? insertPosition - 1 : insertPosition;

      if (adjustedPosition !== currentIndex) {
        moveCard(draggedCardId, adjustedPosition);
      }
    }
    setDraggedCardId(null);
  };

  const handleBackgroundDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";

    // Show insertion point when dragging over background
    if (draggedCardId && moveCard) {
      const insertPosition = calculateInsertPosition(e.clientX);
      setInsertionIndex(insertPosition);
      setHoveredCardId(null); // Clear card hover when over background
    }
  };

  const handleClick = (cardId: CardId) => {
    if (mobile) {
      // On mobile, toggle tooltip visibility
      if (tooltipCardId === cardId) {
        setTooltipCardId(null);
      } else {
        setMousePosition({
          x: window.innerWidth / 2,
          y: window.innerHeight / 2,
        });
        setTooltipCardId(cardId);
      }
    }

    // Always handle card selection
    if (cards.length > 0 && cards[0].cardId !== cardId) {
      swapCards(cards[0].cardId, cardId);
    }
  };

  const isCardCombinable = (cardIndex: number): boolean => {
    return combiningInfo?.combinableIndices.has(cardIndex) ?? false;
  };

  const handleMouseEnter = (e: React.MouseEvent, cardId: CardId) => {
    if (!mobile) {
      setMousePosition({ x: e.clientX, y: e.clientY });
      setTooltipCardId(cardId);
    }
  };

  const handleMouseLeave = () => {
    if (!mobile) {
      setTooltipCardId(null);
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!mobile) {
      setMousePosition({ x: e.clientX, y: e.clientY });
    }
  };

  if (!cards.length) return undefined;
  return (
    <>
      <div
        ref={handRef}
        className="relative w-full h-20 sm:h-32"
        onDrop={!mobile ? handleBackgroundDrop : undefined}
        onDragOver={!mobile ? handleBackgroundDragOver : undefined}
        onTouchMove={mobile ? touchHandlers.onTouchMove : undefined}
      >
        {cards.map((c, idx) => (
          <React.Fragment key={`card-fragment-${c.cardId}`}>
            {/* Insertion indicator before this card */}
            {insertionIndex === idx && (
              <div
                className="absolute w-1 bg-blue-500 h-16 sm:h-32 z-10 rounded"
                style={{
                  left: `${getCardPosition(idx) - 2}px`,
                  top: 0,
                }}
              />
            )}
            <CardEl
              {...c}
              index={idx}
              leftPosition={getCardPosition(idx)}
              selected={idx == 0}
              isDragging={draggedCardId === c.cardId}
              isCombinable={isCardCombinable(idx)}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragOver={handleDragOver}
              onDragEnter={handleCardDragEnter}
              onDragLeave={handleCardDragLeave}
              onDrop={handleDrop}
              onTouchStart={touchHandlers.onTouchStart}
              onTouchMove={touchHandlers.onTouchMove}
              onTouchEnd={touchHandlers.onTouchEnd}
              isHovered={hoveredCardId === c.cardId}
              onClick={handleClick}
              onMouseEnter={(e) => handleMouseEnter(e, c.cardId)}
              onMouseLeave={handleMouseLeave}
              onMouseMove={handleMouseMove}
            />
          </React.Fragment>
        ))}

        {/* Insertion indicator at the end */}
        {insertionIndex === cards.length && (
          <div
            className="absolute w-1 bg-blue-500 h-16 sm:h-32 z-10 rounded"
            style={{
              left: `${getCardPosition(cards.length)}px`,
              top: 0,
            }}
          />
        )}
      </div>

      {/* Render tooltip for the active card */}
      {tooltipCardId &&
        (() => {
          const activeCard = cards.find((c) => c.cardId === tooltipCardId);
          if (activeCard) {
            const playable = GetPlayable(activeCard.name);
            return (
              <PlayableTooltip
                playable={playable}
                visible={true}
                mousePosition={mousePosition}
                source="hand"
              />
            );
          }
          return null;
        })()}
    </>
  );
};

export default Hand;
